import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["defaultValue", "onChange"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useState } from 'react';
import { CALLBACK_FREQUENCY, INPUT_SIZES } from '../../constants';
import { ControlledCombobox } from './ControlledCombobox';

/**
 * @subcomponents [`ComboboxInput`, `ComboboxOption`]
 * @since 8.28.0
 */
export var Combobox = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var defaultValue = props.defaultValue,
    onChange = props.onChange,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(defaultValue || ''),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var onChangeCallback = useCallback(function (nextValue, meta) {
    setValue(nextValue);
    if (typeof onChange === 'function') {
      onChange(nextValue, meta);
    }
  }, [onChange]);
  return /*#__PURE__*/React.createElement(ControlledCombobox, _extends({
    ref: forwardedRef,
    value: value,
    onChange: onChangeCallback
  }, rest));
});
Combobox.displayName = 'Combobox';
Combobox.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * For a typical `<input>` within a `<form>`, pressing "enter" will submit the form. For a
   * combobox, "enter" is used both to select options and submit the form; by default, "enter"
   * will select an option while the dropdown is open, and submit a form while the dropdown is
   * closed.
   *
   * If you enable this prop, pressing "enter" will both select an option and submit a form while
   * the dropdown is open. This can be useful for search box like experiences that act immediately
   * on free form input.
   */
  allowSubmitWhileOpen: PropTypes.bool,
  /**
   * By default, comboboxes have the appearance of a `Select`
   * component (with an arrow indicator). You can change the appearance to `"input"` to match
   * the `Input` component.
   */
  appearance: PropTypes.oneOf(['input', 'select']),
  /**
     * Autocomplete behavior is different from the native
     * [`autoComplete`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
     * attribute, which handles browser autofill (always disabled for this component),
     * and the [`aria-autocomplete`](https://www.w3.org/TR/wai-aria-1.2/#aria-autocomplete)
     * attribute, which will be set automatically to `"list"` or `"both"` depending on the behavior.
     *
     * `"none"`: The combobox is editable, and when the popup is triggered, the suggested
     * values it contains are the same regardless of the characters typed in the combobox. For
     * example, the popup suggests a set of recently entered values, and the suggestions do not
     * change as the user types.
      * `"manual"`: When the popup is triggered, it presents
     * suggested values. If the combobox is editable, the suggested values complete or logically
     * correspond to the characters typed in the combobox. The character string the user has
     * typed will become the value of the combobox unless the user selects a value in the popup.
     *
     * `"automatic"`: The combobox is editable, and when the popup
     * is triggered, it presents suggested values that complete or logically correspond to the
     * characters typed in the combobox, and the first suggestion is automatically highlighted
     * as selected. The automatically selected suggestion becomes the value of the combobox when
     * the combobox loses focus unless the user chooses a different suggestion or changes the
     * character string in the combobox (single-select comboboxes only).
     */
  autoCompleteBehavior: PropTypes.oneOf(['none', 'manual', 'automatic']),
  /**
   * Sometimes we need to render the input's dropdown outside of the parent, if so, set to `true`
   * and the dropdown will render as a direct child of the `body` element
   *
   * @default false
   */
  isDropdownPortal: PropTypes.bool,
  /**
   * The default value of the combobox when used
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * This should be a string, or an array of strings for a multiselectable combobox.
   */
  defaultValue: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  /**
   * By default, values must match one of the given options. To allow free form values to be
   * entered, enable this prop.
   *
   * For single-select comboboxes, enabling this will trigger a change event for every character
   * typed.
   *
   * For multiselectable comboboxes, this will only trigger a change when the value is selected
   * with "enter".
   */
  freeForm: PropTypes.bool,
  /**
   * When enabled, the dropdown will be fluid to match the width of the combobox. Disable this and
   * the width of the dropdown will be based on the dropdown content.
   */
  fluidDropdown: PropTypes.bool,
  /**
   * When enabled, the first option will automatically receive focus when the dropdown is opened.
   */
  focusFirstOption: PropTypes.bool,
  /**
   * By default, options will be highlighted based in the typed input. You can override the
   * default behavior by providing a function that returns a pattern based on the one parameter
   * `inputValue`. You can disable this by setting this to `null`.
   *
   * Note: if you are returning a regular expression, be careful to escape the user input.
   *
   * See `Highlighter` for more information
   * regarding the returned pattern.
   */
  getHighlightPattern: PropTypes.func,
  /**
   * You can limit the number of tags that are visible for multiselectable comoboboxes by
   * passing a number limit. Note: All tags will be visible when the comobobox has focus.
   */
  limitTagsShown: PropTypes.number,
  /**
   * Set to `true` to display the `renderLoadingState` content.
   */
  loading: PropTypes.bool,
  /**
   * A function that maps option values to option props. See
   * `Listbox` for more
   * information.
   */
  mapOptionToProps: PropTypes.func,
  /**
   * Called whenever the value changes with `value` and a `meta` object as parameters.
   *
   * - `value`
   *     - (single-select) the string value
   *     - (multiselectable) an array of string values
   * - `meta` will be an object including `meta.reason` detailing why the change occurred (see
   *    `COMBOBOX_EVENT_REASON` for all options). When the reason is `LISTBOX_EVENT`, the meta
   *    object will also include `meta.listboxEventReason` and `meta.option`.
   *
   * Note: When `freeForm` is enabled for a single-select combobox, this will be called for every
   * character typed, acting like a regular `Input` component.
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * Called whenever the focused option changes in the
   * `Listbox` dialog.
   *
   * See `Listbox` for more information.
   */
  onOptionFocus: PropTypes.func,
  /**
   * Called whenever an option is selected in the `Listbox`
   * dialog.
   *
   * By default, this will only be called when the action results in a change of value.
   * You can use `selectCallbackFrequency` to change this behavior to always trigger.
   *
   * Note: This will NEVER be called for `freeForm` selections
   * (use `onChange` to monitor those values).
   *
   * See `Listbox` for more information.
   */
  onOptionSelect: PropTypes.func,
  /**
   * By default, options will be filtered automatically when using the `"manual"` or `"automatic"`
   * `autoCompleteBehavior`. You can override the default filter behavior by
   * providing your own function that returns a
   * `Listbox` `optionFilter`.
   *
   * The function will be passed the `option` object and the current `inputValue` as the only
   * parameters, and should return `true` to keep, or `false` to discard the option.
   */
  optionFilter: PropTypes.func,
  /**
   * An array of options, or an array of optgroups that will be passed to the
   * `Listbox` dialog. See
   * `Listbox` for more information.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  options: PropTypes.array.isRequired,
  /**
   * Comboboxes are editable by default. Disabling this makes this a
   * `DropdownSelect`.
   *
   * @ignore
   */
  editable: PropTypes.bool,
  /**
   * Empty state is displayed when there are no options, or all the options have been filtered
   * out. You can render your own custom empty state by providing a function that returns markup,
   * or you can disable showing any dropdown by setting this to `null`.
   */
  renderEmptyState: PropTypes.func,
  /**
   * The `Listbox` group render callback.
   */
  renderGroup: PropTypes.func,
  /**
   * The `Listbox` group label render callback.
   */
  renderGroupLabel: PropTypes.func,
  /**
   * You can customize the content rendered when `loading` is true by passing a function that
   * returns your custom markup.
   */
  renderLoadingState: PropTypes.func,
  /**
   * By default, a `ComboboxInput` will be
   * rendered as the text input for the combobox. You can render your own custom
   * input by passing a render function. The render function will receive an object
   * of props that would have been passed directly to `ComboboxInput`.
   */
  renderInput: PropTypes.func,
  /**
   * By default, combobox renders a `ComboboxOption`.
   * You can override this by providing your own render function that will be passed directly as
   * the `Listbox` option render callback.
   */
  renderOption: PropTypes.func,
  /**
   * By default, a `ComboboxInput` will render default Tag
   * component in multiselectable mode. You can override it by providing your own render function.
   */
  renderTag: PropTypes.func,
  /*
   * By default, `onOptionSelect` will only fire if the action results in a change of value.
   * Alternatively, you can set this to `CALLBACK_FREQUENCY.ALWAYS` and the callback will
   * always fire regardless of the outcome on the value.
   *
   * Note: When `CALLBACK_FREQUENCY.ALWAYS` is enabled, you can check if the value will
   * change by looking at the `onOptionSelect` `meta.valueChanged` property.
   */
  selectCallbackFrequency: PropTypes.oneOf(Object.values(CALLBACK_FREQUENCY)),
  /**
   * Can be used when you have options where `value` differs from `label`.
   * If true, Combobox uses the selected option's `label` as the input field's value.
   * If false (default), the selected option's `value` will be used instead.
   *
   * **Note:** If you choose the use `label` as the input field's value, you will need
   * to handle the conversion from label-to-value in your app state or on form submission.
   */
  showLabelForValue: PropTypes.bool,
  /**
   * The combobox input size.
   */
  size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
  /**
   * The value of the combobox when used as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * This should be a string for a single-select combobox, and an array of strings for a
   * multiselectable combobox.
   */
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
} : {};

// Unfortunately, some default props are currently defined both here and in ControlledCombobox.
// We want ControlledComobobox to be the source of truth, but react-styleguidist requires defaults
// to be defined here to be documented.
Combobox.defaultProps = {
  appearance: 'select',
  autoCompleteBehavior: 'automatic',
  isDropdownPortal: false,
  fluidDropdown: true,
  selectCallbackFrequency: CALLBACK_FREQUENCY.CHANGE,
  showLabelForValue: false,
  size: INPUT_SIZES.md
};